import React, { createContext, useContext, useLayoutEffect, useMemo, useState } from 'react';

const ThemeContext = createContext(null);

const FONT_SIZES = ['small', 'medium', 'large'];

const FONT_SIZE_PX = {
  small: '14px',
  medium: '16px',
  large: '20px',
};

function readStoredFontSize() {
  if (typeof window === 'undefined') return 'medium';
  const saved = localStorage.getItem('fontSize');
  return FONT_SIZES.includes(saved) ? saved : 'medium';
}

function readStoredTheme() {
  if (typeof window === 'undefined') return 'light';
  const savedTheme = localStorage.getItem('theme');
  if (savedTheme === 'light' || savedTheme === 'dark') return savedTheme;
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

function applyThemeClass(theme) {
  if (typeof document === 'undefined') return;
  const next = theme === 'dark' ? 'dark' : 'light';
  const root = document.documentElement;
  root.classList.remove('light', 'dark');
  root.classList.add(next);
  root.style.colorScheme = next;
}

function applyFontSize(size) {
  const root = document.documentElement;
  const value = FONT_SIZE_PX[size] || FONT_SIZE_PX.medium;
  root.dataset.fontSize = size;
  root.style.setProperty('--font-size-base', value);
  root.style.fontSize = value;
}

export function ThemeProvider({ children }) {
  const [theme, setTheme] = useState(readStoredTheme);
  const [fontSize, setFontSizeState] = useState(readStoredFontSize);

  useLayoutEffect(() => {
    applyThemeClass(theme);
    localStorage.setItem('theme', theme);
  }, [theme]);

  useLayoutEffect(() => {
    applyFontSize(fontSize);
    localStorage.setItem('fontSize', fontSize);
  }, [fontSize]);

  const toggleTheme = () => {
    setTheme((prev) => (prev === 'light' ? 'dark' : 'light'));
  };

  const setLightTheme = () => setTheme('light');
  const setDarkTheme = () => setTheme('dark');

  const setFontSize = (size) => {
    if (FONT_SIZES.includes(size)) setFontSizeState(size);
  };

  const value = useMemo(
    () => ({
      theme,
      toggleTheme,
      setLightTheme,
      setDarkTheme,
      isDark: theme === 'dark',
      fontSize,
      setFontSize,
      fontSizes: FONT_SIZES,
    }),
    [theme, fontSize]
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used within ThemeProvider');
  return ctx;
}
